import {
  InvalidMarketParamsError,
  UnknownFactory,
  UnknownOfFactory,
  UnsupportedVaultV2AdapterError
} from '@morpho-org/blue-sdk'
import { ORPCError } from '@orpc/client'
import { defaultShouldDehydrateQuery, isServer, QueryClient } from '@tanstack/react-query'

import { isDeterministicEvmError } from '@/lib/observability/client/trackers/rpc-usage/error-classifier'

import { hashQueryKey } from './hashQueryKey'

const MAX_TRANSIENT_RETRIES = 2

/**
 * oRPC errors worth retrying: an unreachable upstream, or any undeclared 5xx (a gateway
 * timeout, a crashed function, an error a resolver failed to type). Declared errors below
 * 500 and `UPSTREAM_SCHEMA` are deterministic answers: the same request fails the same way.
 */
function isTransientORPCError(error: ORPCError<string, unknown>): boolean {
  if (error.defined) return error.code === 'UPSTREAM_UNAVAILABLE'
  return error.status >= 500
}

/** SDK errors derived from on-chain state: a block-pinned read fails the same way every time. */
function isDeterministicSdkError(error: unknown): boolean {
  return (
    error instanceof InvalidMarketParamsError ||
    error instanceof UnknownFactory ||
    error instanceof UnknownOfFactory ||
    error instanceof UnsupportedVaultV2AdapterError
  )
}

/**
 * Browser retry policy: transient oRPC errors and network or batch-decoding failures
 * (`TypeError`, e.g. a stream cut at the route's time limit) share one bounded policy;
 * other oRPC errors, deterministic EVM failures (on-chain reverts, code-size-limit
 * rejections) and deterministic SDK errors (derived from on-chain state, so a
 * block-pinned read fails identically) are never retried; everything else keeps the
 * library default.
 */
export function shouldRetryQueryError(failureCount: number, error: unknown): boolean {
  if (error instanceof ORPCError) {
    return isTransientORPCError(error) && failureCount < MAX_TRANSIENT_RETRIES
  }
  if (error instanceof TypeError) return failureCount < MAX_TRANSIENT_RETRIES
  if (isDeterministicEvmError(error) || isDeterministicSdkError(error)) return false
  return failureCount < 3
}

/**
 * Creates a new TanStack Query client with configured defaults.
 * @returns Configured QueryClient instance
 */
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // oRPC query keys can embed bigints and dates; hash them as oRPC's guide does.
        queryKeyHashFn: hashQueryKey,
        // No default staleTime - queries should explicitly configure their own staleTime
        // This allows queries that shouldn't be cached to work correctly,
        // while prefetched queries can set staleTime explicitly to avoid refetching on client
        // Server keeps TanStack's default of no retries.
        retry: isServer ? 0 : shouldRetryQueryError
      },
      dehydrate: {
        // Include pending queries in dehydration
        shouldDehydrateQuery: query =>
          defaultShouldDehydrateQuery(query) || query.state.status === 'pending',
        // Next.js server errors must not be caught/redacted here — that is how
        // Next.js detects dynamic pages. It also auto-redacts them with its own digests.
        shouldRedactErrors: () => false
      }
    }
  })
}

let browserQueryClient: QueryClient | undefined = undefined

/**
 * Gets or creates a singleton QueryClient instance.
 * Creates a new client on server, reuses existing client in browser.
 * @returns QueryClient instance
 */
export function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient()
    }
    return browserQueryClient
  }
}
